import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, Observable, OperatorFunction, switchMap } from 'rxjs';
import { CannotContainOnlySpaceValidator } from 'src/app/directives/cannot-contain-only-space-validator.directive';
import { OnlyLettersAndNumbersValidator } from 'src/app/directives/only-letters-and-numbers-validator.directive';
import { StringHelper } from 'src/app/directives/string-helper';
import { VerifyAsEmailValidator } from 'src/app/directives/verify-as-email-validator.directive';
import { UserSearchResult } from 'src/app/models/user';
import { OrganisationApiClient } from 'src/app/services/organisation.apiclient';
import { RequestBuilderService } from '../request-builder.service';
import { IdentityProviderConstants } from 'src/app/constants/identity-provider.constants';
import { VerifyAsMobileValidator } from 'src/app/directives/verify-as-mobile-validator.directive';

@Component({
  selector: 'app-person-dialog',
  templateUrl: './person-dialog.component.html'
})
export class PersonDialogComponent implements OnInit {
  stringHelper = new StringHelper();
  person: any;
  form: UntypedFormGroup;
  firstNameControl: FormControl;
  prefixControl: FormControl;
  lastNameControl: FormControl;
  emailControl: FormControl;
  phoneControl: FormControl;
  /**
   * When Identity providers are limited this holds the only allowed providers
   * If empty, use the default set
   */
  allowedIdentityProviders!: string[];

  personalNoteControl: FormControl;
  personLanguage: string = 'Language.Dutch';
  identificationMethod: string = IdentityProviderConstants.Default;
  validateRealIdentityOption: boolean = false;
  @Output('PersonUpdated') personUpdated = new EventEmitter();
  @Input('PersonToEdit') personToEdit: any;
  @Input('showMessage') showMessage!: boolean;
  @Input('organisationSettings') organisationSettings!: any;
  @Input('allowAddAnother') allowAddAnother: boolean = true;
  model!: any;
  providerName!: any;

  searchFirstname: OperatorFunction<string, UserSearchResult[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      switchMap((term) =>
        term.length < 2 ? [] : this.organisationApiClient.getContacts(term, 'firstname')
      ),
    );

  searchLastname: OperatorFunction<string, UserSearchResult[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      switchMap((term) =>
        term.length < 2 ? [] : this.organisationApiClient.getContacts(term, 'lastname')
      ),
    );

  searchEmail: OperatorFunction<string, UserSearchResult[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      switchMap((term) =>
        term.length < 2 ? [] : this.organisationApiClient.getContacts(term, 'email')
      ),
    );

  constructor(private formBuilder: UntypedFormBuilder, public activeModal: NgbActiveModal, private organisationApiClient: OrganisationApiClient, private requestBuilderService: RequestBuilderService) {
    this.person = {};

    this.firstNameControl = new FormControl('', [Validators.required, Validators.maxLength(50), OnlyLettersAndNumbersValidator(), CannotContainOnlySpaceValidator()]);
    this.prefixControl = new FormControl('', [Validators.maxLength(10), OnlyLettersAndNumbersValidator()]);
    this.lastNameControl = new FormControl('', [Validators.required, Validators.maxLength(50), OnlyLettersAndNumbersValidator(), CannotContainOnlySpaceValidator()]);
    this.emailControl = new FormControl('', [Validators.required, VerifyAsEmailValidator(), Validators.maxLength(50)]);
    this.phoneControl = new FormControl('', [Validators.required, CannotContainOnlySpaceValidator(), Validators.maxLength(15),VerifyAsMobileValidator()]);
    this.personalNoteControl = new FormControl({ value: '', disabled: true }, [Validators.maxLength(500)]);
    this.form = this.formBuilder.group(
      {
        firstname: this.firstNameControl,
        prefix: this.prefixControl,
        lastname: this.lastNameControl,
        email: this.emailControl,
        mobile: this.phoneControl,
        language: new UntypedFormControl(''),
        identificationMethod: new UntypedFormControl(''),
        message: this.personalNoteControl,
        showPersonalNote: new UntypedFormControl(false)
      });
    this.allowedIdentityProviders = this.requestBuilderService.onlyAllowedIdentityProviders;
  }

  ngOnInit(): void {
    if (this.personToEdit) {
      // Copy personToEdit-object, so it is not changed before saving.
      this.person = { ...this.personToEdit };
      this.setPersonLanguage(this.person.language);
      this.setIdentityProvider(this.person.identityProvider, this.person.validateRealIdentity);
      this.form.controls['firstname'].setValue(this.person.firstname);
      this.form.controls['prefix'].setValue(this.person.prefix);
      this.form.controls['lastname'].setValue(this.person.lastname);
      this.form.controls['email'].setValue(this.person.email);
      this.form.controls['mobile'].setValue(this.person.mobile);
      this.person.message = this.stringHelper.ReplaceBrToRN(this.person.message);
      this.form.controls['message'].setValue(this.person.message);
      if (this.person.message) {
        this.form.controls['message'].enable();
        this.form.controls['showPersonalNote'].setValue(true);
      }
    } else {
      this.setPersonLanguage('NL');
      this.setIdentityProvider(this.allowedIdentityProviders && this.allowedIdentityProviders.length > 0 ? this.allowedIdentityProviders[0] : IdentityProviderConstants.Default, false);
    }
    this.getOrganisationOptions();
  }

  toggleShowPersonalNote(event: any) {
    const isChecked = event && event.target && event.target.checked;
    if (isChecked) {
      this.form.controls['message'].enable();
    } else {
      this.person.message = '';
      this.form.controls['message'].setValue('');
      this.form.controls['message'].disable();
    }
  }

  setPersonLanguage(language: string) {
    if (language === 'NL') {
      this.person.language = 'NL';
      this.personLanguage = 'Language.Dutch';
    } else {
      this.person.language = 'EN';
      this.personLanguage = 'Language.English';
    }
  }

  setIdentityProvider(provider: string, validateRealIdentity: boolean) {
    if (provider === 'iDIN') this.providerName = IdentityProviderConstants.IDIN;
    else this.providerName = provider;

    this.person.validateRealIdentity = validateRealIdentity;
    this.person.identityProvider = provider;
    this.identificationMethod = this.providerName;
  }

  close() {
    this.activeModal.close();
  }

  save(addAnotherPerson: boolean = false) {
    if (this.identificationMethod === 'iDIN') this.providerName = IdentityProviderConstants.IDIN;
    else this.providerName = this.identificationMethod;

    this.person.firstname = this.firstNameControl.value;
    this.person.lastname = this.lastNameControl.value;
    this.person.prefix = this.prefixControl.value;
    this.person.email = this.emailControl.value;
    this.person.mobile = this.phoneControl.value;
    this.person.message = this.personalNoteControl.value;
    if (this.identificationMethod) {
      this.person.identityProvider = this.providerName;
    }
    this.activeModal.close({ person: this.person, addAnother: addAnotherPerson });
  }

  prefillCustomer(event: NgbTypeaheadSelectItemEvent) {
    event.preventDefault();
    let user: UserSearchResult = event.item;
    if (user !== null) {
      if (user.firstname === undefined) return user;
      if (user) {
        this.form.patchValue({
          firstname: user.firstname,
          prefix: user.prefix ?? '',
          lastname: user.lastname,
          mobile: user.mobile,
          email: user.email
        });
      }
      this.person = user;
    }
    return null;
  }

  getOrganisationOptions() {
    if (!this.organisationSettings) return;
    for (let i: number = 0; i < this.organisationSettings.length; i++) {
      switch (this.organisationSettings[i].key) {
        case "OrganisationLanguage":
          this.personLanguage = this.organisationSettings[i].value.toUpperCase();
          if (this.personLanguage === 'NL') {
            this.person.language = 'NL';
            this.personLanguage = 'Language.Dutch';
          } else {
            this.person.language = 'EN';
            this.personLanguage = 'Language.English';
          }
          break;
        case "ValidateRealIdentity":
          this.validateRealIdentityOption = this.organisationSettings[i].value == "False" ? false : true;
          break;
      }
    }
  }
  /**
   * When having a singular/zero IDP disable the dropdown toggle
   * @returns true if nothing to choose
   */
  cannotChooseIdp() {
    return this.allowedIdentityProviders && this.allowedIdentityProviders.length < 2;
  }

}
